import {
  HHChip,
  HHIconButton,
  HHStack,
} from '@hinge-health/react-component-library';
import { FilterList } from '@mui/icons-material';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import {
  BILL_DATE_FORMAT,
  SubmissionManagementGridFilterModelType,
} from '../../bill-grid-components/bill-management-types';
import { defaultSubmissionManagementGridFilterModel } from './submission-management-grid';

export interface SubmissionQuickFiltersProps {
  filterParams: SubmissionManagementGridFilterModelType;
  setFilterParams: (params: SubmissionManagementGridFilterModelType) => void;
  filterToggle: () => void;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
}

const submissionDateQuickFilters = [
  {
    displayName: 'Submitted within 30 days',
    value: {
      submissionStartDate: dayjs().subtract(30, 'day').format(BILL_DATE_FORMAT),
      submissionEndDate: dayjs().format(BILL_DATE_FORMAT),
    },
  },
  {
    displayName: 'Submitted within 90 days',
    value: {
      submissionStartDate: dayjs().subtract(90, 'day').format(BILL_DATE_FORMAT),
      submissionEndDate: dayjs().format(BILL_DATE_FORMAT),
    },
  },
  {
    displayName: 'Submitted within 6 months',
    value: {
      submissionStartDate: dayjs()
        .subtract(180, 'day')
        .format(BILL_DATE_FORMAT),
      submissionEndDate: dayjs().format(BILL_DATE_FORMAT),
    },
  },
];

export const SubmissionQuickFilters = ({
  filterToggle,
  setFilterParams,
  filterParams,
  setPaginationModel,
}: SubmissionQuickFiltersProps): JSX.Element => {
  const setQuickDateFilterParams = (
    quickFilterInput: Record<string, string>,
  ): void => {
    setPaginationModel({ page: 0, pageSize: 100 });
    const { submissionStartDate } = filterParams;
    if (
      !dayjs(submissionStartDate).isSame(
        dayjs(quickFilterInput.submissionStartDate),
      )
    ) {
      setFilterParams({
        ...filterParams,
        submissionStartDate: quickFilterInput.submissionStartDate,
        submissionEndDate: quickFilterInput.submissionEndDate,
      });
    } else {
      setFilterParams({
        ...filterParams,
        submissionStartDate: null,
        submissionEndDate: null,
      });
    }
  };

  const customFiltersApplied =
    filterParams.submissionIds.length > 0 ||
    filterParams.billIds.length > 0 ||
    filterParams.userIds.length > 0 ||
    filterParams.transactionIds.length > 0 ||
    filterParams.netsuiteIds.length > 0 ||
    filterParams.creditMemoIds.length > 0 ||
    filterParams.submissionStatuses.length > 0 ||
    filterParams.sourceIds.length > 0 ||
    filterParams.clientIds.length > 0 ||
    filterParams.insurerIds.length > 0 ||
    filterParams.partnershipIds.length > 0 ||
    filterParams.sourceStartDate !== null ||
    filterParams.sourceEndDate !== null ||
    (submissionDateQuickFilters.every(
      dateQuickFilter =>
        dateQuickFilter.value.submissionStartDate !==
        filterParams.submissionStartDate,
    ) &&
      filterParams.submissionStartDate !==
        defaultSubmissionManagementGridFilterModel.submissionStartDate);

  return (
    <HHStack direction="row" alignItems="center" spacing={2}>
      {submissionDateQuickFilters.map(filter => {
        const isSameDate =
          filterParams?.submissionStartDate ===
          filter.value.submissionStartDate;
        return (
          <HHChip
            clickable
            key={filter.displayName}
            label={filter.displayName}
            size="small"
            hhVariant={isSameDate ? 'filled' : 'outlined'}
            color={isSameDate ? 'neutral' : 'default'}
            onClick={(): void => setQuickDateFilterParams(filter.value)}
          />
        );
      })}
      {customFiltersApplied ? (
        <HHChip
          key="custom-filters"
          clickable
          label="Custom filter applied"
          size="small"
          hhVariant="filled"
          color="neutral"
          onClick={filterToggle}
        />
      ) : null}
      <HHIconButton
        hhVariant="variant-bypass"
        size="medium"
        color="default"
        aria-label="filterToggle"
        onClick={filterToggle}
      >
        <FilterList />
      </HHIconButton>
    </HHStack>
  );
};
